/**
 * iter_195 — Admin-only Online Viewer Modal.
 *
 * Opens from the Online pill in the Community Pulse widget (admin
 * viewers only — non-admins never see the trigger). Shows the actual
 * identities behind the Online / Live / Away / BRB / Recently Active
 * counts with:
 *   • profile photo
 *   • artist name (+ email for admins)
 *   • current status pill (Online / Live / Away / BRB)
 *   • "Active now" or "Last seen 2 minutes ago"
 *   • current public room link when Live
 *
 * Backed by GET /api/admin/presence/list?filter=<f>. Membership /
 * billing data is never fetched or shown here.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

const FILTERS = [
  { id: "online", label: "Online" },
  { id: "live", label: "Live" },
  { id: "away", label: "Away" },
  { id: "brb", label: "BRB" },
  { id: "recent", label: "Recently Active" },
];

const STATUS_CHIP = {
  online: "bg-emerald-500/20 text-emerald-100 border-emerald-400/40",
  live: "bg-rose-500/20 text-rose-100 border-rose-400/40",
  away: "bg-amber-500/20 text-amber-100 border-amber-400/40",
  brb: "bg-violet-500/20 text-violet-100 border-violet-400/40",
  recent: "bg-white/10 text-gray-200 border-white/20",
};

const AdminOnlineViewerModal = ({ open, onClose, initialFilter = "online" }) => {
  const [filter, setFilter] = useState(initialFilter);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/presence/list?filter=${f}&limit=200`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Admin access required" : "Failed to load presence list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load(filter);
  }, [open, filter, load]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent
        className="bg-gray-950 border border-purple-500/30 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="admin-online-viewer-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Presence Viewer
            <span className="text-[11px] font-normal text-purple-300/80 ml-auto">
              Admin · canonical
            </span>
          </DialogTitle>
          <DialogDescription className="text-[11px] text-gray-400">
            Canonical presence rule · 10-minute freshness window · deduped by user id.
          </DialogDescription>
        </DialogHeader>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 pt-1 pb-3 border-b border-white/10">
          {FILTERS.map((f) => {
            const isActive = f.id === filter;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`admin-online-filter-${f.id}`}
                aria-pressed={isActive}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  isActive
                    ? "bg-purple-500 border-purple-300 text-white"
                    : "bg-white/5 border-white/10 text-gray-300 hover:bg-purple-500/20"
                }`}
              >
                {f.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => load(filter)}
            disabled={loading}
            data-testid="admin-online-refresh"
            aria-label="Refresh presence list"
            className="ml-auto inline-flex items-center justify-center h-7 w-7 rounded-full border border-purple-400/40 text-purple-200 bg-purple-500/10 hover:bg-purple-500/25 disabled:opacity-50 transition"
          >
            <span aria-hidden className={`text-sm leading-none ${loading ? "animate-spin" : ""}`}>↻</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto pt-2" data-testid="admin-online-list">
          {loading && !data && (
            <div className="py-10 text-center text-gray-400 text-sm">Loading presence…</div>
          )}
          {error && (
            <div className="py-10 text-center text-rose-300 text-sm" data-testid="admin-online-error">
              {error}
            </div>
          )}
          {!loading && data && data.count === 0 && (
            <div className="py-10 text-center text-gray-500 text-sm" data-testid="admin-online-empty">
              Nobody in this bucket right now.
            </div>
          )}
          {data && data.users?.length > 0 && (
            <ul className="divide-y divide-white/10">
              {data.users.map((u) => {
                const chip = STATUS_CHIP[u.presence_status] || STATUS_CHIP.online;
                return (
                  <li
                    key={u.user_id}
                    className="flex items-center gap-3 py-2.5 px-1 hover:bg-white/5 rounded"
                    data-testid={`admin-online-row-${u.user_id}`}
                  >
                    <Link to={`/artist/${u.user_id}`} onClick={() => onClose && onClose()} className="flex-shrink-0">
                      {u.profile_photo ? (
                        <img
                          src={optimizeImage(u.profile_photo, 'avatar')}
                          alt={u.artist_name}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center text-white font-bold">
                          {(u.artist_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </Link>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/artist/${u.user_id}`}
                          onClick={() => onClose && onClose()}
                          className="font-semibold text-white truncate hover:text-emerald-300"
                        >
                          {u.artist_name}
                        </Link>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase border ${chip}`}
                          data-testid={`admin-online-status-${u.user_id}`}
                        >
                          {u.presence_status}
                        </span>
                        {u.role === "admin" && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold bg-purple-600 text-white">
                            ADMIN
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-0 mt-0.5">
                        <span data-testid={`admin-online-lastseen-${u.user_id}`}>{u.last_seen_label}</span>
                        {u.email && <span className="opacity-70">· {u.email}</span>}
                        {u.presence_status_note && (
                          <span className="text-amber-200/80 truncate max-w-[240px]">
                            · {u.presence_status_note}
                          </span>
                        )}
                        {u.live_room && (
                          <Link
                            to={`/beams/${u.live_room.room_id}`}
                            onClick={() => onClose && onClose()}
                            className="text-rose-300 hover:text-rose-200 font-semibold"
                          >
                            · 🔴 {u.live_room.title || "Live room"}
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2 text-[11px] text-gray-500">
          <span data-testid="admin-online-count">
            {data ? `${data.count} in ${filter}` : ""}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
            data-testid="admin-online-close"
            className="border-white/20 text-gray-200 hover:bg-white/10 h-7"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminOnlineViewerModal;
